import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectAPI, uploadFile, fetchUrlMetadata } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { FileUpload } from '../components/FileUpload';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Github, ExternalLink, Tag, Loader2, X, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreateProject() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    github_url: '',
    demo_url: '',
    tags: [],
    images: [],
    files: [],
  });
  const [tagInput, setTagInput] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Auto-fetch metadata when URL is entered
  const handleUrlBlur = async (urlType) => {
    const url = formData[urlType];
    if (!url || url.trim().length < 10) return;
    
    // Don't auto-fetch if we already have a description
    if (formData.description.trim().length > 20) return;
    
    setIsFetchingMetadata(true);
    try {
      const response = await fetchUrlMetadata(url);
      const metadata = response.data;
      
      if (metadata.success) {
        const updates = {};
        
        // Auto-fill title if empty
        if (!formData.title.trim() && metadata.title) {
          updates.title = metadata.title;
        }
        
        // Auto-fill description if empty or very short
        if ((!formData.description.trim() || formData.description.trim().length < 20) && metadata.description) {
          updates.description = metadata.description;
        }
        
        // Auto-add topics as tags (for GitHub repos)
        if (metadata.topics && metadata.topics.length > 0 && formData.tags.length === 0) {
          updates.tags = metadata.topics.slice(0, 5);
        }
        
        // Auto-add language as tag if available
        if (metadata.language && !updates.tags?.includes(metadata.language.toLowerCase())) {
          const currentTags = updates.tags || formData.tags;
          if (currentTags.length < 5) {
            updates.tags = [...currentTags, metadata.language.toLowerCase()];
          }
        }
        
        if (Object.keys(updates).length > 0) {
          setFormData(prev => ({ ...prev, ...updates }));
          toast.success('Auto-filled project details from URL!');
        }
      }
    } catch (error) {
      // Silently fail - user can still enter manually
      console.log('Could not fetch URL metadata:', error);
    } finally {
      setIsFetchingMetadata(false);
    }
  };

  // Manual fetch button handler
  const handleFetchMetadata = async () => {
    // Prioritize demo_url since it's the first field
    const url = formData.demo_url || formData.github_url;
    if (!url) {
      toast.error('Please enter a Live Demo or GitHub URL first');
      return;
    }
    
    setIsFetchingMetadata(true);
    try {
      const response = await fetchUrlMetadata(url);
      const metadata = response.data;
      
      if (metadata.success) {
        const updates = {};
        
        if (metadata.title) {
          updates.title = metadata.title;
        }
        
        if (metadata.description) {
          updates.description = metadata.description;
        }
        
        if (metadata.topics && metadata.topics.length > 0) {
          updates.tags = metadata.topics.slice(0, 5);
        }
        
        if (metadata.language && formData.tags.length < 5) {
          const currentTags = updates.tags || formData.tags;
          if (!currentTags.includes(metadata.language.toLowerCase())) {
            updates.tags = [...currentTags, metadata.language.toLowerCase()].slice(0, 5);
          }
        }
        
        setFormData(prev => ({ ...prev, ...updates }));
        toast.success('Project details fetched successfully!');
      } else {
        toast.error('Could not fetch metadata from URL');
      }
    } catch (error) {
      toast.error('Failed to fetch URL metadata');
    } finally {
      setIsFetchingMetadata(false);
    }
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag) && formData.tags.length < 5) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove)
    }));
  };

  const handleFilesChange = (files) => {
    const images = files.filter(f => f.content_type?.startsWith('image/'))
      .map(f => `${BACKEND_URL}/api/files/${f.file_id}`);
    const otherFiles = files.filter(f => !f.content_type?.startsWith('image/'));
    
    setFormData(prev => ({
      ...prev,
      images: images,
      files: otherFiles,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a project title');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a project description');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await projectAPI.create(formData);
      toast.success('Project created successfully!');
      navigate(`/projects/${response.data.project_id}`);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please sign in to create a project');
        navigate('/login');
      } else {
        toast.error('Failed to create project');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="create-project-page">
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Create New Project</CardTitle>
            <CardDescription>
              Share your hackathon project with the community
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="create-project-form">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Project Title *</Label>
                <Input
                  id="title"
                  name="title"
                  placeholder="My Awesome Project"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  data-testid="project-title-input"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Describe your project, what problem it solves, technologies used..."
                  value={formData.description}
                  onChange={handleChange}
                  required
                  className="min-h-[150px]"
                  data-testid="project-description-input"
                />
              </div>

              {/* Demo URL - First URL field */}
              <div className="space-y-2">
                <Label htmlFor="demo_url" className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Live Demo URL
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="demo_url"
                    name="demo_url"
                    type="url"
                    placeholder="https://myproject.com"
                    value={formData.demo_url}
                    onChange={handleChange}
                    onBlur={() => handleUrlBlur('demo_url')}
                    data-testid="project-demo-input"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleFetchMetadata}
                    disabled={isFetchingMetadata || (!formData.github_url && !formData.demo_url)}
                    title="Auto-fill from URL"
                    data-testid="fetch-metadata-btn"
                  >
                    {isFetchingMetadata ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Enter your project URL and we'll auto-fill the details
                </p>
              </div>

              {/* GitHub URL */}
              <div className="space-y-2">
                <Label htmlFor="github_url" className="flex items-center gap-2">
                  <Github className="w-4 h-4" />
                  GitHub Repository
                </Label>
                <Input
                  id="github_url"
                  name="github_url"
                  type="url"
                  placeholder="https://github.com/username/repo"
                  value={formData.github_url}
                  onChange={handleChange}
                  onBlur={() => handleUrlBlur('github_url')}
                  data-testid="project-github-input"
                />
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Tags (up to 5)
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add a tag"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddTag(e)}
                    data-testid="tag-input"
                  />
                  <Button type="button" variant="outline" onClick={handleAddTag}>
                    Add
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="hover:text-indigo-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* File Upload */}
              <div className="space-y-2">
                <Label>Images & Files</Label>
                <FileUpload
                  onFilesChange={handleFilesChange}
                  maxFiles={10}
                />
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="submit-project-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Project'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
